import { useEffect, useRef, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { FullPageSpinner } from '@/components/FullPageSpinner';
import { ErrorPane } from '@/components/ErrorPane';
import { ReauthPrompt } from '@/auth/ReauthPrompt';
import { authFailureCopy, classifyAuthFailure, type AuthFailureKind } from '@/auth/authFailure';
import { hasUnsavedWork } from '@/auth/unsavedWork';
import {
  clearRecoveryMarker,
  continueReauth,
  markRecoveryAttempt,
  recoveryAllowed,
  requestReauth,
  resetReauth,
  useReauthEscalated,
  useReauthState,
} from '@/auth/reauth';
import { Routes } from '@/constants/routes';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  loadMe,
  selectUserStatus,
  selectUserError,
  selectUserErrorKind,
  selectUserErrorStatus,
} from '@/store/userSlice';

interface AuthGateProps {
  children: ReactNode;
}

// How many silent auto-retries a TRANSIENT /api/me failure gets before the user
// sees anything at all (offline blip, api still booting, a 502 from a restart).
const ME_RETRY_LIMIT = 2;
const ME_RETRY_BASE_MS = 600;

// AuthGate wraps the entire app and enforces:
//   1) OIDC user is loaded (react-oidc-context handles the redirect dance).
//   2) /api/me has been fetched into Redux (so Topbar + ProtectedRoute can
//      read roles synchronously).
// Anything below this gate can assume `selectUser()` is non-null and
// `selectUserStatus() === 'ready'`.
//
// Session recovery (LOCKED plan 2026-08-24-auth-session-recovery, W1/D):
// AuthGate no longer decides HOW to get a lost session back — every such site
// delegates to the re-auth coordinator (`@/auth/reauth`), which owns
// single-flight, the hidden-tab gate, unsaved-work protection, `returnTo` and
// the loop guard. AuthGate only decides WHAT TO RENDER while that runs, and the
// answer is a spinner — never the old "Načtení profilu selhalo" pane, which is
// now reserved for a genuine outage (loop guard tripped / KC unreachable).
//
// BRANCH-ORDER INVARIANT (do not reorder without reading this):
// the "session lost, keep the page alive" branch renders `children` while
// `auth.isAuthenticated` is FALSE. Two branches above it are load-bearing
// guards, not cosmetics:
//   1. `reauthState === 'prompt'` — must win so the prompt is drawn.
//   2. `auth.isLoading || auth.activeNavigator` — must win during
//      `signoutRedirect()`, which react-oidc-context DOES wrap in
//      `activeNavigator`. Without it, a logout would keep painting the
//      departing user's name, e-mail and roles. The `loggedOut()` dispatch in
//      `Topbar.handleLogout` (whole-store reset at the root reducer) is the
//      belt to this braces; both are tested.
export const AuthGate = ({ children }: AuthGateProps): React.JSX.Element => {
  const auth = useAuth();
  const { t } = useTranslation('auth');
  const dispatch = useAppDispatch();
  const userStatus = useAppSelector(selectUserStatus);
  const userError = useAppSelector(selectUserError);
  const userErrorKind = useAppSelector(selectUserErrorKind);
  const userErrorStatus = useAppSelector(selectUserErrorStatus);
  const location = useLocation();
  const reauthState = useReauthState();
  // Second bounce-back or later — the prompt says the login service looks down.
  const reauthEscalated = useReauthEscalated();

  // While react-oidc-context is parsing the `?code=...&state=...` query on
  // the redirect_uri, we MUST NOT trigger another signinRedirect — that
  // would wipe sessionStorage and lose the PKCE verifier.
  const onCallbackRoute = location.pathname === Routes.CALLBACK;

  // Trigger /api/me once we have an access token. Re-runs if the user
  // signs out + in as somebody else (sub changes).
  useEffect(() => {
    if (auth.isAuthenticated && userStatus === 'idle') {
      void dispatch(loadMe());
    }
  }, [auth.isAuthenticated, auth.user?.profile.sub, userStatus, dispatch]);

  // "Was there unsaved work on screen?", captured during the RENDER phase.
  //
  // Neither of the two obvious places works. Reading it from the effect below
  // is too late: losing the session makes this component stop rendering
  // `children`, and React runs the editor's unmount cleanup (which unregisters
  // its unsaved-work key) in the same commit whose passive effect fires — the
  // registry is always empty by then. And reading it only while
  // `auth.isAuthenticated` is stale: AuthGate does NOT re-render while the user
  // types, so the last authenticated render predates the edits.
  //
  // The render phase of the session-loss render is the one moment both are
  // true: `auth.isAuthenticated` has already flipped, and the children (with
  // their registrations) are still mounted — they unmount in the commit that
  // follows. Hence: read on every render, unconditionally.
  const unsavedAtSessionLoss = useRef(false);
  unsavedAtSessionLoss.current = hasUnsavedWork();

  // Did this tab ever hold a session? Distinguishes a RECOVERY (a session that
  // existed and died — loop guard applies, "Obnovuji přihlášení…") from a COLD
  // START (first visit — no guard, "Přesměrování na přihlášení…"). Guarding a
  // cold start told anyone who pressed Back on the Keycloak login page that the
  // "Přihlašovací služba je nedostupná", which was simply false.
  const everAuthenticated = useRef(false);
  if (auth.isAuthenticated) everAuthenticated.current = true;

  // No usable session outside the callback route — cold start, a silent renew
  // that gave up, or a `userUnloaded` after the SSO session died. All three are
  // the same question ("get the session back"), so all three go to the one
  // coordinator. It is sticky + single-flight, so re-renders are free.
  useEffect(() => {
    if (onCallbackRoute) return;
    if (auth.isLoading || auth.activeNavigator) return;
    if (auth.isAuthenticated) return;
    void requestReauth({
      unsavedWork: unsavedAtSessionLoss.current,
      recovery: everAuthenticated.current,
    });
  }, [
    onCallbackRoute,
    auth.isLoading,
    auth.activeNavigator,
    auth.isAuthenticated,
  ]);

  // Self-heal a stale/failed callback: if the code exchange failed while we're
  // on the callback route (a home-screen launch replaying a dead code, or any
  // lost PKCE state), redirect to a clean login once. Guarded so a fresh login
  // that *also* fails surfaces the error instead of looping. Shares the ONE
  // recovery marker with the coordinator (`@/auth/reauth`).
  const recoveringCallback = !!auth.error && onCallbackRoute;
  // True for the whole mount once we trigger a recovery redirect, so the render
  // keeps showing the spinner (not the error pane) until navigation completes.
  const recoveryInFlight = useRef(false);
  useEffect(() => {
    if (recoveringCallback && !recoveryInFlight.current && recoveryAllowed()) {
      recoveryInFlight.current = true;
      markRecoveryAttempt();
      void auth.signinRedirect({ state: { returnTo: Routes.HOME } });
    }
  }, [recoveringCallback, auth]);

  // Clear the recovery marker + coordinator state once the session is proven
  // END TO END — not merely when Keycloak hands us a token.
  //
  // `isAuthenticated` is too early: it fires before /api/me answers. If the api
  // rejects a perfectly fresh KC token (audience/issuer mismatch, a stale JWKS
  // cache, clock skew — the classic post-deploy failure class), clearing here
  // would re-arm the guard on every pass and produce an unbounded loop:
  // authenticated → marker cleared → /api/me 401 → recover → live SSO cookie →
  // instant bounce back → repeat, as fast as the page loads. Waiting for
  // `ready` (i.e. `loadMe.fulfilled`) makes the second lap trip the guard and
  // surface a real error instead.
  useEffect(() => {
    if (userStatus === 'ready') {
      clearRecoveryMarker();
      resetReauth();
    }
  }, [userStatus]);

  // /api/me failed. `auth` → dead session, hand it to the coordinator (never an
  // error pane). `transient` → retry a couple of times with a short backoff
  // before showing anything. `fatal` → straight to the pane.
  const meRetries = useRef(0);
  useEffect(() => {
    if (userStatus === 'ready') {
      meRetries.current = 0;
      return;
    }
    if (userStatus !== 'error') return;
    if (userErrorKind === 'auth') {
      void requestReauth({ unsavedWork: unsavedAtSessionLoss.current });
      return;
    }
    if (userErrorKind === 'transient' && meRetries.current < ME_RETRY_LIMIT) {
      meRetries.current += 1;
      const timer = window.setTimeout(
        () => void dispatch(loadMe()),
        ME_RETRY_BASE_MS * meRetries.current,
      );
      return () => {
        window.clearTimeout(timer);
      };
    }
  }, [userStatus, userErrorKind, dispatch]);

  const callbackRecoveryPending =
    recoveringCallback && (recoveryInFlight.current || recoveryAllowed());

  // Honest spinner copy: the first-ever redirect is "Přesměrování na
  // přihlášení…", a recovery is "Obnovuji přihlášení…".
  const recoverySpinner = (): React.JSX.Element => (
    <FullPageSpinner
      label={everAuthenticated.current ? t('reauth.restoring') : t('login.redirectPending')}
    />
  );

  // A REAL failure pane (loop guard tripped / KC unreachable). It names the
  // actual cause — expired login vs. unavailable service vs. no network — and
  // demotes the raw technical string ("Token is not active") to a small
  // secondary line so support still has it without the user having to read it.
  const reauthErrorPane = (detail: string | null): React.JSX.Element => {
    // No technical string at all means the recovery itself gave up (guard
    // tripped). That is an expired login — NOT evidence that Keycloak is down,
    // which is what the `unavailable` fallback would have claimed.
    const kind = detail === null ? 'expired' : classifyAuthFailure(detail);
    const { titleKey, bodyKey } = authFailureCopy(kind);
    return (
      <ErrorPane
        title={t(titleKey)}
        message={t(bodyKey)}
        detail={detail}
        onRetry={() => {
          clearRecoveryMarker();
          resetReauth();
          void requestReauth();
        }}
      />
    );
  };

  // /api/me failed for a non-auth reason. `transient` with no HTTP status never
  // reached a server (offline); with a 5xx the service is down; anything else is
  // a genuine profile-load failure.
  const profileErrorKind = (): AuthFailureKind => {
    if (userErrorKind === 'transient') {
      return userErrorStatus === null ? 'network' : 'unavailable';
    }
    return 'profile';
  };

  // ONE shape for every branch that renders the live app, prompt or not.
  //
  // `children` must occupy the SAME structural position in all of them: React
  // reconciles by position, so moving it (e.g. putting the prompt first, for
  // tab order) remounts the entire page and destroys the unsaved text this
  // whole mechanism exists to protect.
  //
  // The prompt does NOT compensate with focus — it moves focus nowhere at all.
  // `role="alert"` announces it to assistive tech on mount, and the keyboard
  // path to its button is an ordinary Tab. Two separate attempts to be clever
  // here were both wrong; `ReauthPrompt` carries the full post-mortem.
  const livePage = (withPrompt: boolean): React.JSX.Element => (
    <>
      {children}
      {withPrompt ? (
        <ReauthPrompt onContinue={() => void continueReauth()} escalated={reauthEscalated} />
      ) : null}
    </>
  );

  // The unsaved-work prompt outranks every other branch: it is rendered OVER
  // the still-mounted page so the user can copy their edits out before the
  // redirect throws them away (plan W1/E variant (a) — we do not pretend the
  // edits survive). Only possible when a profile is already loaded; without one
  // there is nothing behind the prompt worth preserving.
  if (reauthState === 'prompt') {
    if (userStatus === 'ready') {
      return livePage(true);
    }
    return (
      <ReauthPrompt onContinue={() => void continueReauth()} escalated={reauthEscalated} />
    );
  }

  if (auth.isLoading || auth.activeNavigator) {
    return <FullPageSpinner label={t('login.signingIn')} />;
  }

  // The session died while the app was fully up (profile loaded, user working).
  // KEEP THE PAGE MOUNTED while the coordinator decides what to do.
  //
  // Swapping in a spinner here — even for the single render before the prompt
  // resolves — unmounts the editor and destroys exactly the unsaved text the
  // prompt then invites the user to copy. It also flashes a spinner in front of
  // a redirect that is about to replace the page anyway. Only a `failed`
  // recovery (guard tripped) is allowed to take the page away.
  if (
    !onCallbackRoute &&
    !auth.isAuthenticated &&
    everAuthenticated.current &&
    userStatus === 'ready' &&
    reauthState !== 'failed'
  ) {
    return livePage(false);
  }

  // An OIDC error while a usable session is STILL in hand (a background renew
  // that failed but left the current token valid) is not worth a single pixel:
  // fall through to the normal gates and let the next 401 recover if needed.
  if (auth.error && !auth.isAuthenticated) {
    // On the callback route the stale-code self-heal owns the recovery.
    if (onCallbackRoute) {
      if (callbackRecoveryPending) {
        return <FullPageSpinner label={t('login.signingIn')} />;
      }
      return reauthErrorPane(auth.error.message);
    }
    // Off the callback route an OIDC error is an expired/broken session, NOT
    // something to shout at the user about: the coordinator is already on it.
    // Only a tripped loop guard (or a redirect that itself threw) falls through
    // to a pane — that is a real outage.
    if (reauthState === 'failed') {
      return reauthErrorPane(auth.error.message);
    }
    return recoverySpinner();
  }

  // On the callback route, react-oidc-context is finalising the code
  // exchange synchronously after this render — short-circuit to a spinner
  // so we don't render children with a stale unauthenticated state.
  if (onCallbackRoute && !auth.isAuthenticated) {
    return <FullPageSpinner label={t('callback.processing')} />;
  }

  if (!auth.isAuthenticated) {
    if (reauthState === 'failed') {
      return reauthErrorPane(null);
    }
    return recoverySpinner();
  }

  if (userStatus === 'idle' || userStatus === 'loading') {
    return <FullPageSpinner label={t('login.loadingProfile')} />;
  }

  // Logged out, still waiting for the signout navigation to take the page. The
  // store is already wiped, so there is nothing left to render but a spinner —
  // and critically NOT `children`, which the final fall-through would otherwise
  // hand back.
  if (userStatus === 'loggedOut') {
    return <FullPageSpinner label={t('login.signingOut')} />;
  }

  if (userStatus === 'error') {
    // A 401 on /api/me is a session problem — the coordinator is recovering.
    if (userErrorKind === 'auth') {
      return reauthState === 'failed'
        ? reauthErrorPane(null)
        : recoverySpinner();
    }
    // A transient failure still has retries left → keep the loading spinner up;
    // the user never learns the first attempt bounced.
    if (userErrorKind === 'transient' && meRetries.current < ME_RETRY_LIMIT) {
      return <FullPageSpinner label={t('login.loadingProfile')} />;
    }
    const { titleKey, bodyKey } = authFailureCopy(profileErrorKind());
    return (
      <ErrorPane
        title={t(titleKey)}
        message={t(bodyKey)}
        detail={userError}
        onRetry={() => {
          meRetries.current = 0;
          void dispatch(loadMe());
        }}
      />
    );
  }

  return <>{children}</>;
};
